import {
  Component,
  ContentChildren,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ListItemDirective } from '~/core/directives/list-item.directive';
import { generateUid } from '~/core/helpers/uid/uid.helper';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';

import SwiperCore, { Virtual, A11y, Navigation, SwiperOptions } from 'swiper';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { VtmnPlayIconButtonSizeEnum } from '~/component/buttons/vtmn-play-icon-button/enum/vtmn-play-icon-button-size.enum';
import { VtmnPlayIconButtonVariantEnum } from '~/component/buttons/vtmn-play-icon-button/enum/vtmn-play-icon-button-variant.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';

SwiperCore.use([Virtual, Navigation, A11y]);

@Component({
  selector: 'app-attendee-card-collection',
  templateUrl: './card-collection.component.html',
  styleUrls: ['./card-collection.component.scss'],
})
export class AttendeeCardCollectionComponent implements OnInit, OnDestroy {
  @ContentChildren(ListItemDirective)
  public cards: QueryList<ListItemDirective>;

  @Input() public description: string;
  @Input() public externalLink: string;
  @Input() public link: {
    parameters?: { [key: string]: string | number };
    path: string;
    text: string;
    page: 1;
  };
  @Input() public showCta?: boolean;
  @Input() public title: string;

  protected readonly subscriptions = new Subscription();

  public readonly icon = VtmnPlayIconTypeEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;
  public readonly vtmnPlayIconButtonSize = VtmnPlayIconButtonSizeEnum;
  public readonly vtmnPlayButtonIconVariant = VtmnPlayIconButtonVariantEnum;

  public config: SwiperOptions;
  public id: string = generateUid();
  public isMobileDevice: boolean;

  constructor(protected readonly matchMediaService: MatchMediaService) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
        this.isMobileDevice = isMobileDevice;
      })
    );

    this.config = {
      a11y: {
        enabled: false,
      },
      direction: 'horizontal',
      loop: false,
      slidesPerView: 1.4,
      spaceBetween: 16,
      keyboard: true,
      mousewheel: { forceToAxis: true },
      scrollbar: false,
      pagination: false,
      navigation: {
        prevEl: `#${this.prevCardCollectionButtonId}`,
        nextEl: `#${this.nextCardCollectionButtonId}`,
      },
      breakpoints: {
        601: {
          slidesPerView: 3,
          spaceBetween: 16,
        },
        900: {
          slidesPerView: 4,
          spaceBetween: 16,
        },
        1302: {
          slidesPerView: 4,
          spaceBetween: 24,
        },
      },
    };
  }

  public get linkUrl(): string {
    let queryParams = '';

    if (this.link?.parameters) {
      if (Object.entries(this.link?.parameters)?.length > 0) {
        for (const [key, value] of Object.entries(this.link?.parameters)) {
          if (key === 'placeId') {
            queryParams += `${key}=${encodeURIComponent(value)}&`;
          } else {
            queryParams += `${key}=${value}&`;
          }
        }
      }
    }

    return queryParams !== '' && this.link?.parameters
      ? `${this.link.path}?${queryParams}page=1`
      : `${this.link.path}?page=1`;
  }

  public get nextCardCollectionButtonId(): string {
    return `card-collection-next-button-${this.id}`;
  }

  public get prevCardCollectionButtonId(): string {
    return `card-collection-prev-button-${this.id}`;
  }
}
